import React from 'react';

// @ts-ignore
import styles from './VtsOrder.module.scss?module';
import { Navigate } from 'react-router-dom';
import Confirmation from './Confirmation/Confirmation';
import { Col, Container, Row } from 'react-bootstrap';
import Countdown from '../../Abstracts/Countdown/Countdown';
import { getRequest } from '../../Helper/ApiClient';
import LoginForm from '../../Abstracts/Forms/LoginForm/LoginForm';
import { useUser } from '../../Context/UserContext';
import VtsProvider, { useVts } from '../../Context/VtsContext';
import LoadingSpinner from '../../Abstracts/LoadingSpinner/LoadingSpinner';
import RemainingBasket from './RemainingBasket/RemainingBasket';
import ReleaseBasket from './ReleaseBasket/ReleaseBasket';
import PlaceOrderSuccessBanner from './PlaceOrderSuccessBanner/PlaceOrderSuccessBanner';

const formatVtsDateTime = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

function VtsOrderContent() {
  const userContext = useUser();
  const { vts } = useVts();
  const orderServiceViewRequest = getRequest(
    '/api/onlineshop/order_service_view',
    undefined,
    { enabled: !!userContext.user }
  );
  const orderYear = vts?.order_start
    ? new Date(vts.order_start).getFullYear()
    : '';
  // TODO: "final order release"-Frist (typischerweise 30. Juni) ist nicht in
  // der vts-Tabelle abgebildet. Wenn sich der Termin ändert, hier anpassen
  // bzw. perspektivisch ins vts-Schema aufnehmen.
  const finalOrderReleaseDate = '30th of June 2026';

  if (userContext.loading || orderServiceViewRequest.isLoading) {
    return (
      <Container className={'py-6 text-center'}>
        <Row>
          <Col>
            <LoadingSpinner />
          </Col>
        </Row>
      </Container>
    );
  }

  if (!userContext.user) {
    return (
      <Container className={'py-6'}>
        <Row>
          <Col>
            <h1 className={'fst-italic fw-bolder'}>
              Virtual trade show - order confirmation
            </h1>
          </Col>
        </Row>
        <Row className={'pt-4'}>
          <Col lg={6}>
            <LoginForm />
          </Col>
        </Row>
      </Container>
    );
  }

  if (userContext.user.is_exhibitor) {
    return <Navigate to="/virtual-trade-show/lobby" replace />;
  }

  // Backend liefert null wenn kein aktives VTS existiert oder die Session
  // serverseitig (noch) keinen User hat. Ohne Guard crasht der data.view-Zugriff.
  if (!orderServiceViewRequest.data) {
    return (
      <Container className={'py-6'}>
        <Row>
          <Col>
            <h1 className={'fst-italic fw-bolder'}>Virtual trade show</h1>
          </Col>
        </Row>
      </Container>
    );
  }

  if (orderServiceViewRequest.data.view === 'no_baskets') {
    return (
      <>
        <Container>
          <Row className={'pt-6 pb-5'}>
            <Col>
              <h1 className={'fst-italic fw-bolder'}>
                Virtual Trade Show - order confirmation
              </h1>
            </Col>
          </Row>
        </Container>
        <Container className={'pb-6'}>
          <Row>
            <Col>
              Too bad that you didn´t take part at the Virtual Trade Show
              {orderYear ? ` ${orderYear}` : ''}. We hope to see you next year!
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (
    orderServiceViewRequest.data.view === 'before_basket_confirmation' ||
    orderServiceViewRequest.data.view === 'basket_confirmation' ||
    orderServiceViewRequest.data.view === 'confirmation_period_end'
  ) {
    return (
      <>
        <Container>
          <Row className={'pt-6 pb-5'}>
            <Col>
              <h1 className={'fst-italic fw-bolder'}>
                Virtual Trade Show - order confirmation
              </h1>
            </Col>
          </Row>
          <Row className={'pt-5 text-center fs-6'}>
            <Col>
              Confirmation period begins on{' '}
              {formatVtsDateTime(vts?.confirmation_start)} and finishes on{' '}
              {formatVtsDateTime(vts?.confirmation_end)}.
            </Col>
          </Row>
        </Container>
        {orderServiceViewRequest.data.view === 'before_basket_confirmation' &&
          vts?.confirmation_start && (
            <Container>
              <Row>
                <Col className={'text-center'}>
                  <Countdown dateString={vts.confirmation_start} />
                </Col>
              </Row>
            </Container>
          )}
        {orderServiceViewRequest.data.view === 'basket_confirmation' && (
          <Confirmation />
        )}
        {orderServiceViewRequest.data.view === 'confirmation_period_end' && (
          <Container className={'pb-6'}>
            <Row>
              <Col>
                The confirmation period is over.
                <br />
                Too bad that you didn't confirm your VTS quantities on time.
                <br />
                We hope to see you next year.
              </Col>
            </Row>
          </Container>
        )}
      </>
    );
  }

  if (
    orderServiceViewRequest.data.view === 'release_basket' ||
    orderServiceViewRequest.data.view === 'remaining_basket'
  ) {
    return (
      <>
        <PlaceOrderSuccessBanner />
        <Container style={{ marginTop: '3.5rem' }}>
          <Row>
            <Col>
              <h1>
                Virtual Trade Show - Order release (until {finalOrderReleaseDate})
              </h1>
              <p>
                With the list below you can release your Virtual Trade Show order
                or parts of it. Several partial releases are possible and will be
                invoiced accordingly.
                <br />
                Final order release is {finalOrderReleaseDate}.
                <br />
                In regards to product availability, we advise you to be fast:
                FIRST COME FIRST SERVE.
              </p>
              <p style={{ color: 'red' }}>
                All products not released until {finalOrderReleaseDate} will be
                deleted from your basket.
              </p>

              {orderServiceViewRequest.data.view === 'remaining_basket' && (
                <RemainingBasket defineView={orderServiceViewRequest.refetch} />
              )}
              {orderServiceViewRequest.data.view === 'release_basket' && (
                <ReleaseBasket defineView={orderServiceViewRequest.refetch}/>
              )}
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (orderServiceViewRequest.data.view === 'total_released') {
    return (
      <>
        <PlaceOrderSuccessBanner />
        <Container>
          <Row className={'py-6'}>
            <Col>
              <h1 className={'pb-4 fst-italic fw-bolder fs-2'}>
                Virtual Trade Show - Order release
              </h1>
              <p>
                Thanks for your participation. All orders have been shipped.
                <br />
                We hope we can welcome you again next year!
              </p>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <Container className={'py-6'}>
      <Row>
        <Col>
          <h1 className={'fst-italic fw-bolder'}>
            Virtual trade show
          </h1>
        </Col>
      </Row>
    </Container>
  );
}

export default function VtsOrder() {
  return (
    <VtsProvider>
      <VtsOrderContent />
    </VtsProvider>
  );
}
